/**
 * AnalyticsBootstrap
 *
 * Wires the vendor-agnostic `analytics` wrapper into the React tree. Mount once
 * under `<AuthProvider>` and `<BrowserRouter>` so it can react to auth state
 * and emit pageviews on navigation.
 *
 * Lifecycle:
 *   1. On sign-in: fetch the tenant's `analytics_enabled` setting, then
 *      `analytics.init(...)` (no-op if disabled or no API key).
 *   2. After init: identify the user and join the tenant group.
 *   3. On every route change: `analytics.pageview(path)`.
 *   4. On sign-out: `analytics.reset()` clears identity.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../auth';
import axiosInstance from '../api/axiosInstance';

import { analytics } from './index';

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;

// Suppress analytics on local dev hosts so test/dev traffic doesn't pollute
// the production PostHog project. Native Capacitor builds (no window.location)
// are unaffected.
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local')
  );
}

interface TenantSettingsResponse {
  analytics_enabled: boolean;
}

export function AnalyticsBootstrap(): null {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const initialisedFor = useRef<string | null>(null);

  // Init + identify on auth, reset on logout.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (initialisedFor.current) {
        analytics.reset();
        initialisedFor.current = null;
      }
      return;
    }
    // Keyed on the emulation flag as well as the id: a session that starts as
    // the customer and is later corrected to an emulation (or the reverse) is
    // the SAME user id, so an id-only key would pin the first, wrong answer for
    // the tab's lifetime and never re-identify.
    const identity = `${user.id}:${user.is_emulating === true}`;
    if (initialisedFor.current === identity) return;

    let cancelled = false;
    void (async () => {
      let tenantOptIn = true;
      // The tenant-settings route is `require_auth`, which 401s a principal with
      // no tenant — a `pending_setup` user in the Setup Wizard has none yet.
      // There is no tenant to read an opt-out from, so skip the ask entirely.
      if (user.tenant_id) {
        try {
          const resp = await axiosInstance.get<TenantSettingsResponse>(
            '/api/v1/integrations/tenant-settings',
          );
          tenantOptIn = resp.data.analytics_enabled !== false;
        } catch {
          // Backend may be unreachable or endpoint missing on older deploys —
          // fall back to opt-in (the API key still gates this).
        }
      }

      const optIn = tenantOptIn && !isLocalDevHost();
      await analytics.init(POSTHOG_API_KEY, optIn);
      if (cancelled || !analytics.isReady()) return;

      const emailDomain = user.email?.includes('@')
        ? user.email.split('@')[1].toLowerCase()
        : undefined;

      analytics.identify({
        user_id: user.id,
        tenant_id: user.tenant_id,
        email_domain: emailDomain,
        preferred_language: user.preferred_language,
        country: user.country,
        is_admin: user.is_admin,
        // During emulation `/auth/me` returns the EMULATED user, so without
        // this the session is attributed to the customer's user and tenant
        // group with nothing marking it as ours. The flag is server-computed
        // from the token's emulation claim — never read from localStorage,
        // which the SPA can only guess at.
        is_emulating: user.is_emulating === true,
      });

      if (user.tenant_id) {
        analytics.group(user.tenant_id);
      }

      initialisedFor.current = identity;
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  // Pageviews on every route change (only after analytics is ready).
  useEffect(() => {
    if (!analytics.isReady()) return;
    analytics.pageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}
